import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Minus, TrendingUp, Quote } from "lucide-react";

// ============= PALETTE BRAND COLUCCI =============
const COL = {
  bg: "#0F1F14",
  card: "#163321",
  accent: "#D97B58",   // ocra
  warn: "#B84A3C",     // rosso terra
  highlight: "#E5C97B",// giallo caldo
  muted: "#7A8B82",
  light: "#FCFCFA",    // cream
  lightMuted: "#4A5E51",
};

// ============= PALETTE GEOPOP =============
const GEO = {
  yellow: "#FFDB3B",   // giallo brand Geopop
  dark: "#1A1A1A",
  blue: "#2653FF",
  pink: "#FF3B8F",
  cream: "#FFF8E6",
};

// ============= STEMMA COLUCCI =============
function StemmaColucci({ className = "", inverted = false }) {
  const color = inverted ? COL.bg : "#FFF";
  return (
    <svg viewBox="0 0 24 24" className={className} style={{ width: 14, height: 14 }}>
      <path d="M12 1 L22 7 L22 17 L12 23 L2 17 L2 7 Z" fill="none" stroke={color} strokeWidth="1.8" />
      <path d="M12 6 L17 9 L17 15 L12 18 L7 15 L7 9 Z" fill={color} />
      <text x="12" y="15" textAnchor="middle" fontSize="6.5" fontWeight="900" fill={inverted ? "#FFF" : COL.bg}>C</text>
    </svg>
  );
}

// ============= FRAME COMUNE (Stile Colucci) =============
function SlideFrame({ index, total = 7, children, bg = COL.bg, light = false }) {
  const textColor = light ? COL.bg : "#FFF";
  const mutedColor = light ? COL.lightMuted : COL.muted;
  const gridColor = light ? "rgba(15,31,20,0.5)" : "rgba(255,255,255,0.5)";
  return (
    <div
      className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl"
      style={{ backgroundColor: bg, color: textColor, fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(${gridColor} 1px, transparent 1px), linear-gradient(90deg, ${gridColor} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />
      <div
        className="absolute -top-10 -right-10 w-[260px] h-[260px] rounded-full opacity-10 blur-[80px]"
        style={{ backgroundColor: COL.accent }}
      />

      {/* Top bar con stemma */}
      <div className="absolute top-4 left-5 right-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StemmaColucci inverted={light} />
          <span className="text-[9px] font-bold tracking-[0.25em] uppercase" style={{ color: mutedColor }}>
            Colucci Assicuratori
          </span>
        </div>
        <span className="text-[9px] font-mono font-semibold" style={{ color: mutedColor }}>
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </div>

      <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between">
        <span className="text-[9px] font-mono font-semibold uppercase tracking-widest" style={{ color: mutedColor }}>
          @colucci_assicuratori
        </span>
        <span className="text-[9px] font-mono" style={{ color: mutedColor }}>
          colucciassicuratori.com
        </span>
      </div>

      <div className="absolute inset-0 px-6 pt-14 pb-14">{children}</div>
    </div>
  );
}

// ============= SLIDE COLUCCI STYLE =============
const Slide1 = () => (
  <SlideFrame index={0}>
    <div className="h-full flex flex-col justify-between">
      <div>
        <div className="inline-block px-2 py-0.5 rounded text-[9px] font-black tracking-widest uppercase mb-4"
          style={{ backgroundColor: COL.highlight, color: COL.bg }}>
          INSURANCE · ALERT
        </div>
        <h2 className="font-black leading-[0.9] tracking-tight" style={{ fontSize: 40, letterSpacing: "-0.03em" }}>
          Furti in<br />
          <span className="italic font-light" style={{ color: COL.muted }}>abitazione</span><br />
          Italia&nbsp;2025.
        </h2>
      </div>
      <div>
        <div className="flex items-end gap-2 mb-1">
          <ArrowUp className="w-9 h-9 mb-1" style={{ color: COL.accent, strokeWidth: 3 }} />
          <span className="font-black leading-none"
            style={{ fontSize: 88, color: COL.accent, letterSpacing: "-0.04em", fontVariantNumeric: "tabular-nums" }}>
            +18<span style={{ fontSize: 48 }}>%</span>
          </span>
        </div>
        <p className="text-xs font-mono uppercase tracking-widest" style={{ color: COL.muted }}>
          vs anno precedente · fonte ANSA
        </p>
      </div>
    </div>
  </SlideFrame>
);

const Slide2 = () => {
  const data = [
    { y: "2021", v: 42 }, { y: "2022", v: 48 }, { y: "2023", v: 56 },
    { y: "2024", v: 65 }, { y: "2025", v: 100, highlight: true },
  ];
  return (
    <SlideFrame index={1}>
      <div className="h-full flex flex-col">
        <h2 className="font-black leading-[0.95] tracking-tight mb-1" style={{ fontSize: 28, letterSpacing: "-0.02em" }}>1 furto ogni</h2>
        <h2 className="font-black leading-[0.9] tracking-tight mb-4" style={{ fontSize: 62, color: COL.accent, letterSpacing: "-0.04em" }}>4 minuti.</h2>
        <div className="flex items-end gap-3 flex-1 pb-3">
          {data.map((d) => (
            <div key={d.y} className="flex-1 flex flex-col items-center justify-end gap-2">
              <span className="text-[10px] font-mono font-bold" style={{ color: d.highlight ? COL.accent : COL.muted }}>
                {d.y === "2025" ? "156K" : ""}
              </span>
              <div className="w-full rounded-t-sm"
                style={{ height: `${(d.v / 100) * 100}%`,
                  backgroundColor: d.highlight ? COL.accent : COL.card,
                  border: d.highlight ? "none" : `1px solid ${COL.muted}33` }} />
              <span className="text-[10px] font-mono" style={{ color: d.highlight ? "#FFF" : COL.muted }}>{d.y}</span>
            </div>
          ))}
        </div>
        <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: COL.muted }}>
          Furti denunciati / anno · ISTAT 2025
        </p>
      </div>
    </SlideFrame>
  );
};

const Slide3 = () => {
  const stats = [
    { v: "€3.800", l: "Danno medio", tdelta: "+22%" },
    { v: "32%", l: "Dei furti in Campania" },
    { v: "41%", l: "Case senza polizza", tdelta: "+3pp" },
  ];
  return (
    <SlideFrame index={2}>
      <div className="h-full flex flex-col">
        <h2 className="font-black leading-[0.95] tracking-tight mb-5" style={{ fontSize: 30, letterSpacing: "-0.02em" }}>
          Lo scenario<br /><span style={{ color: COL.muted }}>in tre numeri.</span>
        </h2>
        <div className="flex-1 space-y-3">
          {stats.map((s, i) => (
            <div key={i} className="rounded-lg p-3.5 flex items-center justify-between"
              style={{ backgroundColor: COL.card, border: "1px solid #ffffff10" }}>
              <div>
                <span className="font-black leading-none block"
                  style={{ fontSize: 30, color: "#FFF", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
                  {s.v}
                </span>
                <span className="text-[10px] uppercase tracking-wider font-semibold mt-1 block" style={{ color: COL.muted }}>{s.l}</span>
              </div>
              <div className="flex items-center gap-1">
                {s.tdelta ? <ArrowUp className="w-4 h-4" style={{ color: COL.warn, strokeWidth: 3 }} /> : <Minus className="w-4 h-4" style={{ color: COL.muted, strokeWidth: 3 }} />}
                {s.tdelta && <span className="text-[10px] font-mono font-bold" style={{ color: COL.warn }}>{s.tdelta}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideFrame>
  );
};

const Slide4 = () => {
  const items = [
    { h: "Casa vuota > 45 giorni", s: "Polizza sospesa in vacanza lunga" },
    { h: "Bici e monopattini fuori", s: "Serve estensione dedicata" },
    { h: "Contanti oltre €1.000", s: "Limite standard contratto" },
  ];
  return (
    <SlideFrame index={3}>
      <div className="h-full flex flex-col">
        <div className="inline-block px-2 py-0.5 rounded text-[9px] font-black tracking-widest uppercase mb-3 self-start"
          style={{ backgroundColor: COL.warn, color: "#FFF" }}>WARNING</div>
        <h2 className="font-black leading-[0.95] tracking-tight mb-5" style={{ fontSize: 32, letterSpacing: "-0.02em" }}>
          Cosa la polizza<br /><span style={{ color: COL.warn }}>NON</span>&nbsp;copre.
        </h2>
        <ul className="space-y-3.5 flex-1">
          {items.map((it, i) => (
            <li key={i} className="flex gap-3 items-start">
              <span className="font-black text-xl leading-none shrink-0 w-6"
                style={{ color: COL.warn, fontVariantNumeric: "tabular-nums" }}>0{i + 1}</span>
              <div className="border-l-2 pl-3" style={{ borderColor: COL.warn + "55" }}>
                <p className="font-semibold text-sm text-white leading-tight">{it.h}</p>
                <p className="text-[11px] mt-0.5" style={{ color: COL.muted }}>{it.s}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </SlideFrame>
  );
};

// === SLIDE 5 - CHECKLIST in TEMA CHIARO CREAM ===
const Slide5 = () => {
  const checks = [
    "Massimale copre il VALORE reale dei beni?",
    "Obbligo di antifurto/casseforte chiaro?",
    "Bici, device, denaro contante inclusi?",
  ];
  return (
    <SlideFrame index={4} bg={COL.light} light>
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4" style={{ color: COL.accent, strokeWidth: 3 }} />
          <span className="text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: COL.accent }}>
            Insurance · Checklist
          </span>
        </div>
        <h2 className="font-black leading-[0.95] tracking-tight mb-5" style={{ fontSize: 30, letterSpacing: "-0.02em", color: COL.bg }}>
          Prima del rinnovo.<br /><span style={{ color: COL.lightMuted }}>3 domande.</span>
        </h2>
        <ul className="space-y-3 flex-1">
          {checks.map((t, i) => (
            <li key={i} className="flex gap-3 items-start">
              <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                style={{ backgroundColor: COL.accent + "22", border: `1.5px solid ${COL.accent}` }}>
                <span className="font-black text-xs" style={{ color: COL.accent }}>{i + 1}</span>
              </div>
              <p className="text-sm leading-snug font-medium pt-0.5" style={{ color: COL.bg }}>{t}</p>
            </li>
          ))}
        </ul>
        <p className="text-[9px] uppercase tracking-widest font-mono mt-3" style={{ color: COL.lightMuted }}>
          Non sostituisce consulenza IVASS personalizzata.
        </p>
      </div>
    </SlideFrame>
  );
};

// === SLIDE 6 - TESTIMONIANZA CHRISTIAN ===
const Slide6 = () => (
  <SlideFrame index={5}>
    <div className="h-full flex flex-col justify-between">
      <div>
        <Quote className="w-10 h-10 mb-3" style={{ color: COL.accent, strokeWidth: 2 }} />
        <p className="font-black leading-[1.1] tracking-tight" style={{ fontSize: 24, letterSpacing: "-0.02em" }}>
          &ldquo;La polizza giusta<br />non e' la piu' economica.<br />
          <span style={{ color: COL.accent }}>E' quella che<br />paga al sinistro.&rdquo;</span>
        </p>
      </div>
      <div className="flex items-center gap-3 pt-4 border-t" style={{ borderColor: "#ffffff15" }}>
        <div className="w-11 h-11 rounded-full overflow-hidden shrink-0" style={{ backgroundColor: COL.card }}>
          <img src="https://www.colucciassicuratori.com/wp-content/uploads/2025/07/WhatsApp-Image-2025-07-11-at-18.11.52-1200x1500.jpeg"
            alt="" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-xs font-bold text-white leading-tight">Christian Colucci</p>
          <p className="text-[10px] font-mono uppercase tracking-widest mt-0.5" style={{ color: COL.muted }}>
            CEO · Redazione · IVASS A000464752
          </p>
        </div>
      </div>
    </div>
  </SlideFrame>
);

const Slide7 = () => (
  <SlideFrame index={6}>
    <div className="h-full flex flex-col justify-center items-start">
      <span className="text-[9px] font-bold tracking-[0.3em] uppercase mb-4" style={{ color: COL.accent }}>
        End · 07 di 07
      </span>
      <h2 className="font-black leading-[0.9] tracking-tight mb-4" style={{ fontSize: 48, letterSpacing: "-0.03em" }}>
        Vuoi essere<br /><span style={{ color: COL.accent }}>davvero</span><br />coperto?
      </h2>
      <p className="text-sm mb-6 leading-snug" style={{ color: COL.muted }}>
        Salva questo post.<br />
        Prenota una consulenza gratuita:<br />
        <span className="text-white font-semibold">leggiamo la tua polizza a voce alta.</span>
      </p>
      <div className="inline-flex items-center gap-3 px-5 py-3 rounded-lg w-full justify-between"
        style={{ backgroundColor: COL.accent, color: COL.bg }}>
        <span className="font-black text-base tracking-tight">081&nbsp;680568</span>
        <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Chiama →</span>
      </div>
      <p className="text-[10px] font-mono uppercase tracking-widest mt-3" style={{ color: COL.muted }}>
        IVASS · A000464752 · Napoli · Portici
      </p>
    </div>
  </SlideFrame>
);

const SLIDES_COLUCCI = [Slide1, Slide2, Slide3, Slide4, Slide5, Slide6, Slide7];
const LABELS_COLUCCI = ["Hook", "Barchart", "3 KPI", "Warning", "Checklist", "Quote", "CTA"];

// ============================================================
// GEOPOP STYLE — stile alternativo pop, colorato, typographic
// ============================================================
function GeoFrame({ index, bg, fg, children, total = 4 }) {
  return (
    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl"
      style={{ backgroundColor: bg, color: fg, fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Grandi elementi decorativi */}
      <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full"
        style={{ backgroundColor: fg, opacity: 0.08 }} />
      <div className="absolute top-3 left-5 text-[10px] font-black tracking-[0.3em] uppercase">
        COLUCCI&nbsp;/&nbsp;POP
      </div>
      <div className="absolute top-3 right-5 text-[10px] font-mono font-bold">
        {index + 1}/{total}
      </div>
      <div className="absolute bottom-3 left-5 right-5 text-[9px] font-black uppercase tracking-widest flex items-center justify-between">
        <span>@colucci_assicuratori</span>
        <span>SWIPE →</span>
      </div>
      <div className="absolute inset-0 px-7 pt-16 pb-12">{children}</div>
    </div>
  );
}

const Geo1 = () => (
  <GeoFrame index={0} bg={GEO.yellow} fg={GEO.dark}>
    <div className="h-full flex flex-col justify-center">
      <div className="inline-flex items-center gap-2 mb-3">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: GEO.dark }} />
        <span className="text-[10px] font-black uppercase tracking-widest">Assicurazione · Fatti</span>
      </div>
      <h1 className="font-black tracking-tight" style={{ fontSize: 56, lineHeight: 0.88, letterSpacing: "-0.04em" }}>
        I furti<br />in casa<br />
        <span style={{
          WebkitTextStroke: `2px ${GEO.dark}`,
          color: GEO.yellow,
          textShadow: `4px 4px 0 ${GEO.dark}`,
        }}>
          crescono!
        </span>
      </h1>
      <div className="mt-4 inline-block px-3 py-1 w-max"
        style={{ backgroundColor: GEO.dark, color: GEO.yellow }}>
        <span className="font-black text-sm">+18% NEL 2025</span>
      </div>
    </div>
  </GeoFrame>
);

const Geo2 = () => (
  <GeoFrame index={1} bg={GEO.blue} fg="#FFF">
    <div className="h-full flex flex-col justify-center items-center text-center">
      <p className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: GEO.yellow }}>
        Un furto ogni
      </p>
      <h2 className="font-black" style={{ fontSize: 130, lineHeight: 0.82, letterSpacing: "-0.06em" }}>
        4'
      </h2>
      <p className="font-black text-xl uppercase tracking-wide mt-2">MINUTI</p>
      <p className="text-xs mt-4 max-w-[80%]" style={{ color: "rgba(255,255,255,0.7)" }}>
        156.000 furti denunciati in Italia nel 2025 (ISTAT)
      </p>
    </div>
  </GeoFrame>
);

const Geo3 = () => (
  <GeoFrame index={2} bg={GEO.pink} fg="#FFF">
    <div className="h-full flex flex-col justify-between">
      <div>
        <div className="inline-block px-2 py-1 rounded bg-black text-white text-[9px] font-black uppercase tracking-widest mb-4">
          ATTENZIONE
        </div>
        <h2 className="font-black leading-[0.9]" style={{ fontSize: 38, letterSpacing: "-0.03em" }}>
          Cosa la polizza<br /><u>NON</u> copre?
        </h2>
      </div>
      <div className="space-y-2">
        {["Casa vuota > 45gg", "Biciclette fuori casa", "Contanti > €1.000"].map((t, i) => (
          <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ backgroundColor: "rgba(0,0,0,0.2)" }}>
            <span className="w-7 h-7 rounded-full bg-white text-black font-black flex items-center justify-center text-sm shrink-0">
              {i + 1}
            </span>
            <span className="font-bold text-sm">{t}</span>
          </div>
        ))}
      </div>
    </div>
  </GeoFrame>
);

const Geo4 = () => (
  <GeoFrame index={3} bg={GEO.cream} fg={GEO.dark}>
    <div className="h-full flex flex-col justify-center items-center text-center">
      <div className="w-16 h-16 rounded-full mb-3 flex items-center justify-center"
        style={{ backgroundColor: GEO.yellow, border: `3px solid ${GEO.dark}` }}>
        <Quote className="w-7 h-7" strokeWidth={3} />
      </div>
      <h2 className="font-black mb-3" style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: "-0.03em" }}>
        Vuoi capire<br />la tua polizza?
      </h2>
      <p className="text-sm font-semibold mb-5 max-w-[85%]" style={{ color: "#555" }}>
        Ti leggiamo il contratto a voce alta.<br />Gratis. Senza impegno.
      </p>
      <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black text-sm shadow-lg"
        style={{ backgroundColor: GEO.dark, color: GEO.yellow }}>
        📞 081 761 1000
      </div>
      <p className="text-[10px] font-mono uppercase tracking-widest mt-3" style={{ color: "#666" }}>
        Colucci Assicuratori · IVASS
      </p>
    </div>
  </GeoFrame>
);

const SLIDES_GEOPOP = [Geo1, Geo2, Geo3, Geo4];
const LABELS_GEOPOP = ["Hook", "Hero", "Warning", "CTA"];

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function CaroselloPreviewDemo() {
  const [style, setStyle] = useState("colucci");
  const [idx, setIdx] = useState(0);

  const SLIDES = style === "colucci" ? SLIDES_COLUCCI : SLIDES_GEOPOP;
  const LABELS = style === "colucci" ? LABELS_COLUCCI : LABELS_GEOPOP;
  const Current = SLIDES[Math.min(idx, SLIDES.length - 1)];
  const currentIdx = Math.min(idx, SLIDES.length - 1);

  const switchStyle = (s) => { setStyle(s); setIdx(0); };

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-6" data-testid="ig-carousel-demo">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-[#0F1F14] border border-[#D97B58]/30">
            <div className="w-1.5 h-1.5 rounded-full bg-[#D97B58] animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D97B58]">Anteprima carosello redazionale</span>
          </div>
          <h3 className="font-heading text-lg font-bold text-zinc-900 mb-1">
            {style === "colucci" ? "Stile Colucci — Editorial Fintech (7 slide)" : "Stile Geopop — Pop Editoriale (4 slide)"}
          </h3>
          <p className="text-sm text-zinc-500 max-w-lg">
            {style === "colucci"
              ? "Verde scuro + ocra + rosso terra del brand. Tipografia audace, grafici minimali, testimonianza del CEO, slide chiara per rompere la monotonia."
              : "Tinta piatta ad alto contrasto, titoloni con outline, frecce giganti, emoji. Stile 'educativo-pop' ispirato a Geopop. Buono per engagement giovane, ma meno autorevole."}
          </p>
        </div>

        {/* Style switcher */}
        <div className="flex rounded-lg border border-zinc-200 overflow-hidden shrink-0" data-testid="style-switcher">
          <button onClick={() => switchStyle("colucci")}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${style === "colucci" ? "bg-[#0F1F14] text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}
            data-testid="style-colucci">
            Colucci
          </button>
          <button onClick={() => switchStyle("geopop")}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${style === "geopop" ? "bg-[#FFDB3B] text-zinc-900" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}
            data-testid="style-geopop">
            Geopop
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-5 items-start">
        <div className="relative max-w-[360px] w-full mx-auto md:mx-0">
          <Current />
          <Button size="icon" variant="secondary" onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0}
            className="absolute top-1/2 -left-3 -translate-y-1/2 rounded-full shadow-md z-10 w-9 h-9" data-testid="demo-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="secondary" onClick={() => setIdx(i => Math.min(SLIDES.length - 1, i + 1))} disabled={currentIdx >= SLIDES.length - 1}
            className="absolute top-1/2 -right-3 -translate-y-1/2 rounded-full shadow-md z-10 w-9 h-9" data-testid="demo-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
          {SLIDES.map((_, i) => {
            const bg = style === "colucci" ? COL.bg : GEO.yellow;
            const active = currentIdx === i;
            return (
              <button key={i} onClick={() => setIdx(i)}
                className={`relative w-[72px] h-[90px] rounded-lg overflow-hidden shrink-0 transition-all ${active ? `ring-2 ${style === "colucci" ? "ring-[#D97B58]" : "ring-[#1A1A1A]"} scale-[1.04]` : "opacity-50 hover:opacity-90"}`}
                style={{ backgroundColor: bg }}
                data-testid={`demo-thumb-${i}`}>
                <div className="absolute top-1 right-1.5 text-[8px] font-mono font-bold" style={{ color: style === "colucci" ? "rgba(255,255,255,0.8)" : "#1A1A1A" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="absolute inset-0 flex items-center justify-center px-1">
                  <span className="text-[7px] font-bold text-center leading-tight uppercase tracking-wider"
                    style={{ color: style === "colucci" ? "rgba(255,255,255,0.6)" : "#1A1A1A" }}>
                    {LABELS[i]}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {style === "colucci" && (
        <div className="mt-6 bg-[#0F1F14] border border-[#D97B58]/20 rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-[#D97B58]" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#D97B58]">Didascalia suggerita</p>
          </div>
          <p className="text-sm leading-relaxed whitespace-pre-line text-white/90">
{`🚨 FURTI IN CASA · ITALIA 2025

+18% rispetto al 2024. 1 furto ogni 4 minuti.
Fonte: ANSA, ISTAT.

Nel carosello:
→ I 3 KPI che devi conoscere
→ Cosa la tua polizza NON copre davvero
→ 3 domande da fare prima del rinnovo
→ Il pensiero del nostro CEO

Salva il post e scrivici in DM per una consulenza gratuita.
IVASS · A000464752 · 📍 Napoli · Portici

#assicurazioni #polizzacasa #casasicura #furtoinabitazione #educazioneassicurativa #colucciassicuratori #napoli #portici`}
          </p>
        </div>
      )}
    </div>
  );
}
